import {useCallback, useState} from 'react'
import {passThroughMapping as defaultPassthroughMapping} from '../../../../../common/action-flows/constants'
import {evaluateMappingVariables} from '../../../../../common/action-flows/evaluate-mapping-variables'
import {flattenTargetUserIds} from '../../../../../common/action-flows/evaluator'
import {resolveContextMapping} from '../../../../../common/action-flows/resolve-context-mapping'
import {createSampleProfileFromMappingFallbacks} from '../../../../../common/action-flows/steps/card-helpers'
import {
  getValue,
  mapGenericNestedPayload
} from '../../../../../common/integrations/payload-mapper'
import {
  ContextMapping,
  GenericNestedMapper,
  Getter,
  GetterCodec
} from '../../../../../common/types'
import {useVariables} from '../../../../../providers/variables/provider'

type State = {
  passthrough: Getter
  script: Getter
  nested: GenericNestedMapper
}

export type UIMappingType = keyof State

type Mapper = Getter | GenericNestedMapper

export type MappedData =
  | {
      valid: false
      errors: string[]
      warnings?: string[]
    }
  | {
      result?: Record<string, unknown>
      valid: true
      warnings?: string[]
    }

/**
 * Useful where we need to display payload which could be either
 * a passthrough (for responses) a GenericMapper or a single script for advanced cases.
 *
 * We store both in a single state object, which allows a user
 * to toggle between the two while editing without losing data
 */
export const useMappingSwitch = (
  mapping: Mapper,
  options: {defaultScript?: string; passThroughMapping?: Getter} = {}
) => {
  // based on the shape of mapping do we have a nested or script* value (see note above)
  const determineType = useCallback(
    (m: Mapper): UIMappingType =>
      GetterCodec.is(m)
        ? m.type === 'script'
          ? 'script'
          : 'passthrough'
        : 'nested',
    []
  )

  const passThroughMapping =
    options.passThroughMapping ?? defaultPassthroughMapping

  const [type, setType] = useState<UIMappingType>(() => determineType(mapping))

  // Store in the appropriate section of state based on the mapper type
  const convert = useCallback(
    (m: Mapper): Partial<State> => {
      if (GetterCodec.is(m)) {
        return {
          script: m.type === 'script' ? m : passThroughMapping
        }
      }
      return {nested: m}
    },
    [passThroughMapping]
  )

  const [state, setInternalState] = useState<State>(() => ({
    //defaults
    passthrough: passThroughMapping,
    script: {type: 'script', script: options.defaultScript ?? ''},
    nested: {},
    // updated with current mapping value
    ...convert(mapping)
  }))

  // Expose this callers, allowing any mapping type to be set
  const setState = useCallback(
    (update: Mapper) => {
      return setInternalState(s => ({
        ...s,
        ...convert(update)
      }))
    },
    [convert]
  )

  const variables = useVariables()

  // Helper function to map data from either type
  const mapData = useCallback(
    async ({
      context,
      contextMapping
    }: {
      context: Record<PropertyKey, unknown>
      contextMapping?: ContextMapping
    }): Promise<MappedData> => {
      if (type === 'nested') {
        const mappingState = state[type]

        // mappingState might have %{variable} placeholders in it
        // so we need to resolve them before we can evaluate the mapping
        const withResolvedVariables =
          contextMapping && Object.keys(contextMapping).length
            ? await evaluateMappingVariables({
                data: resolveContextMapping(context, contextMapping),
                userProfileData:
                  createSampleProfileFromMappingFallbacks(contextMapping),
                variables,
                mapping: mappingState
              })
            : mappingState

        const result = mapGenericNestedPayload(context, withResolvedVariables)
        return result.valid
          ? {
              ...result,
              result: flattenTargetUserIds(
                result.result as Record<string, unknown>
              )
            }
          : result
      }
      const res = getValue(state[type], context)
      // return a consistent shape i.e. {valid:true, result: unknown}
      // makes downstream UI easier
      if (!res.valid) {
        return {...res, warnings: undefined}
      }
      return {
        ...res,
        result: flattenTargetUserIds(res.value as Record<string, unknown>),
        warnings: undefined
      }
    },
    [state, type, variables]
  )

  return {type, setType, value: state, setValue: setState, mapData}
}
